if (!customElements.get('product-details-tabs')) {
  customElements.define(
    'product-details-tabs',
    class ProductDetailsTabs extends HTMLElement {
      connectedCallback() {
        this.tabs = [...this.querySelectorAll('[role="tab"]')];
        this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
        this.createFaqRows();
        this.numberKeyFeatures();
        this.initTouchSwipe();

        this.tabs.forEach((tab, index) => {
          tab.addEventListener('click', (event) => {
            event.preventDefault();
            this.selectTab(index);
          });
          tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
        });

        const activeIndex = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
        this.selectTab(activeIndex >= 0 ? activeIndex : 0);
      }

      addNumberingToItem(element, number) {
        if (!element) return;
        const text = element.textContent.trim();
        if (/^\s*\d+[\.\)]/.test(text)) return;

        const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null, false);
        let node;
        while ((node = walker.nextNode())) {
          if (node.textContent.trim()) {
            const leadingSpaces = node.textContent.match(/^\s*/)[0];
            node.textContent = leadingSpaces + `${number}. ` + node.textContent.trimStart();
            return;
          }
        }
        element.prepend(document.createTextNode(`${number}. `));
      }

      numberKeyFeatures() {
        const featureItems = this.querySelectorAll('.hbc-product-tabs__feature-grid li');
        let counter = 1;
        featureItems.forEach((item) => {
          this.addNumberingToItem(item, counter);
          counter += 1;
        });
      }

      createFaqRows() {
        const faq = this.querySelector('.hbc-product-tabs__faq');
        if (!faq) return;

        const existingRows = faq.querySelectorAll('.hbc-product-tabs__faq-row');
        if (existingRows.length > 0) {
          existingRows.forEach((row, index) => this.addNumberingToItem(row, index + 1));
          return;
        }

        const richTextRows = [...faq.querySelectorAll('.metafield-rich_text_field > p')];
        if (richTextRows.length > 1) {
          richTextRows.forEach((row, index) => {
            row.classList.add('hbc-product-tabs__faq-row');
            this.addNumberingToItem(row, index + 1);
          });
          return;
        }

        const source = faq.querySelector('.metafield-multi_line_text_field') || richTextRows[0];
        if (!source) return;

        const nodes = [...source.childNodes];
        let previousNodeWasBreak = false;
        const hasRowSeparators = nodes.some((node) => {
          if (node.nodeName === 'BR') {
            if (previousNodeWasBreak) return true;
            previousNodeWasBreak = true;
          } else if (node.nodeType !== Node.TEXT_NODE || node.textContent.trim()) {
            previousNodeWasBreak = false;
          }
          return false;
        });

        if (!hasRowSeparators) {
          this.addNumberingToItem(source, 1);
          return;
        }

        const rows = document.createDocumentFragment();
        let row = document.createElement('span');
        let consecutiveBreaks = 0;
        let rowIndex = 0;

        row.className = 'hbc-product-tabs__faq-row';

        const finishRow = () => {
          if (!row.textContent.trim()) return;
          rowIndex += 1;
          this.addNumberingToItem(row, rowIndex);
          rows.append(row);
          row = document.createElement('span');
          row.className = 'hbc-product-tabs__faq-row';
        };

        nodes.forEach((node) => {
          if (node.nodeName === 'BR') {
            consecutiveBreaks += 1;
            if (consecutiveBreaks === 1) row.append(node);
            if (consecutiveBreaks === 2) {
              row.lastChild?.remove();
              finishRow();
            }
            return;
          }

          if (node.nodeType === Node.TEXT_NODE && !node.textContent.trim() && consecutiveBreaks > 0) return;

          consecutiveBreaks = 0;
          row.append(node);
        });

        finishRow();
        source.replaceChildren(rows);
        source.classList.add('hbc-product-tabs__faq-rows');
      }

      selectTab(index, focus = false) {
        this.tabs.forEach((tab, tabIndex) => {
          const selected = tabIndex === index;
          tab.setAttribute('aria-selected', String(selected));
          tab.tabIndex = selected ? 0 : -1;
          if (selected && focus) tab.focus();
        });

        this.panels.forEach((panel, panelIndex) => {
          const isSelected = panelIndex === index;
          panel.hidden = !isSelected;
          if (isSelected) {
            panel.removeAttribute('hidden');
            panel.style.display = '';
          } else {
            panel.setAttribute('hidden', '');
            panel.style.display = 'none';
          }
        });

        this.tabs[index]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }

      onKeydown(event, index) {
        let nextIndex;
        if (event.key === 'ArrowRight') nextIndex = (index + 1) % this.tabs.length;
        if (event.key === 'ArrowLeft') nextIndex = (index - 1 + this.tabs.length) % this.tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = this.tabs.length - 1;
        if (nextIndex === undefined) return;

        event.preventDefault();
        this.selectTab(nextIndex, true);
      }

      goToAdjacentTab(offset) {
        const now = Date.now();
        if (this.lastSwipeTime && now - this.lastSwipeTime < 350) return;

        const currentIndex = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
        const activeIndex = currentIndex >= 0 ? currentIndex : 0;
        const targetIndex = activeIndex + offset;

        if (targetIndex >= 0 && targetIndex < this.tabs.length) {
          this.lastSwipeTime = now;
          this.selectTab(targetIndex);
        }
      }

      initTouchSwipe() {
        let startX = 0;
        let startY = 0;
        let startTime = 0;
        let isHorizontal = null;

        const isMobile = () => window.matchMedia('(max-width: 749px)').matches;

        const reset = () => {
          startX = 0;
          startY = 0;
          startTime = 0;
          isHorizontal = null;
        };

        const onStart = (clientX, clientY, target) => {
          if (!isMobile()) return false;
          // Do not intercept swipe inside nested horizontal carousels (e.g. reviews carousel)
          if (target && target.closest && target.closest('.jdgm-rev-widg__reviews.hbc-review-carousel, .hbc-review-carousel')) {
            return false;
          }
          startX = clientX;
          startY = clientY;
          startTime = Date.now();
          isHorizontal = null;
          return true;
        };

        const onMove = (clientX, clientY, event) => {
          if (!startTime || isHorizontal === false) return;

          const diffX = clientX - startX;
          const diffY = clientY - startY;
          const absX = Math.abs(diffX);
          const absY = Math.abs(diffY);

          if (isHorizontal === null) {
            // Need minimum displacement to decide intention
            if (absX < 8 && absY < 8) return;

            if (absX > absY) {
              isHorizontal = true;
            } else {
              isHorizontal = false;
              return;
            }
          }

          if (isHorizontal && event && event.cancelable) {
            event.preventDefault();
          }
        };

        const onEnd = (clientX, clientY) => {
          if (!startTime || isHorizontal !== true) {
            reset();
            return;
          }

          const diffX = clientX - startX;
          const diffY = clientY - startY;
          const absX = Math.abs(diffX);
          const absY = Math.abs(diffY);
          const duration = Date.now() - startTime;

          reset();

          const isSwipe = absX >= 40 && absX > absY * 1.25 && (duration <= 500 || absX >= 70);
          if (!isSwipe) return;

          // Prevent synthetic click from firing on underlying element
          const suppressClick = (e) => {
            e.preventDefault();
            e.stopPropagation();
          };
          this.addEventListener('click', suppressClick, { capture: true, once: true });
          setTimeout(() => {
            this.removeEventListener('click', suppressClick, { capture: true });
          }, 350);

          if (diffX < 0) {
            this.goToAdjacentTab(1);
          } else {
            this.goToAdjacentTab(-1);
          }
        };

        // Touch events for mobile touch devices
        this.addEventListener(
          'touchstart',
          (event) => {
            if (event.touches.length !== 1) return;
            onStart(event.touches[0].clientX, event.touches[0].clientY, event.target);
          },
          { passive: true }
        );

        this.addEventListener(
          'touchmove',
          (event) => {
            if (!event.touches || event.touches.length !== 1) return;
            onMove(event.touches[0].clientX, event.touches[0].clientY, event);
          },
          { passive: false }
        );

        this.addEventListener(
          'touchend',
          (event) => {
            const touch = event.changedTouches && event.changedTouches[0];
            if (touch) {
              onEnd(touch.clientX, touch.clientY);
            } else {
              reset();
            }
          },
          { passive: true }
        );

        this.addEventListener('touchcancel', reset, { passive: true });

        // Pointer events for desktop testing in mobile viewport (mouse drag)
        if (window.PointerEvent) {
          this.addEventListener(
            'pointerdown',
            (event) => {
              if (event.pointerType === 'touch') return;
              if (event.button !== 0) return;
              if (!onStart(event.clientX, event.clientY, event.target)) return;

              const onPointerMove = (e) => onMove(e.clientX, e.clientY, e);
              const onPointerUp = (e) => {
                window.removeEventListener('pointermove', onPointerMove);
                window.removeEventListener('pointerup', onPointerUp);
                window.removeEventListener('pointercancel', onPointerCancel);
                onEnd(e.clientX, e.clientY);
              };
              const onPointerCancel = () => {
                window.removeEventListener('pointermove', onPointerMove);
                window.removeEventListener('pointerup', onPointerUp);
                window.removeEventListener('pointercancel', onPointerCancel);
                reset();
              };

              window.addEventListener('pointermove', onPointerMove, { passive: false });
              window.addEventListener('pointerup', onPointerUp, { passive: true });
              window.addEventListener('pointercancel', onPointerCancel, { passive: true });
            },
            { passive: true }
          );
        }
      }
    }
  );
}
